import { useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuth } from '../../redux/selectors/commonSelectors.ts';
import { authActions } from '../../redux/slices/authSlice.ts';
import { loginWithCredentials, logoutSession, type CommonDispatch } from '../../redux/thunks/authThunks.ts';
import { loginRequest } from '../../app/msalConfig.ts';
import { LOGIN_PATH, portalHref } from '../../app/portal.ts';
import type { LoginCredentials } from '../../services/auth/authService.ts';
export type { LoginCredentials } from '../../services/auth/authService.ts';
export { toUserInfo } from '../../services/auth/authService.ts';
import { AUTH_MODE } from '../../services/auth/config.ts';
import { getAccessToken, setAccessToken } from '../../services/auth/token.ts';
export { AUTH_MODE } from '../../services/auth/config.ts';
export type { AuthMode } from '../../services/auth/config.ts';
export { getAccessToken, setAccessToken } from '../../services/auth/token.ts';

const IS_DEV_AUTH = AUTH_MODE === 'dev';
const IS_LOCAL_AUTH = AUTH_MODE === 'local' || AUTH_MODE === 'mock-sso';
const IS_MOCK_ENTRA_AUTH = AUTH_MODE === 'mock-entra' || AUTH_MODE === 'mock-msal';

// ── Local credential auth (replaces the legacy forms-authentication cookie) ──
function useLocalAuth() {
  const { user, roles, accessToken, isLoading } = useSelector(selectAuth);
  const dispatch = useDispatch<CommonDispatch>();
  const hasRole = useCallback((role: string) => roles.includes(role), [roles]);
  const hasAnyRole = useCallback((required: string[]) => required.some((role) => roles.includes(role)), [roles]);

  const login = useCallback(
    async (credentials?: LoginCredentials) => {
      // Called without credentials (e.g. from a guard) — the caller renders the form.
      if (!credentials) return;

      await dispatch(loginWithCredentials(credentials));
    },
    [dispatch],
  );

  const logout = useCallback(async () => {
    await dispatch(logoutSession());
    window.location.href = portalHref(LOGIN_PATH);
  }, [dispatch]);

  return {
    user,
    roles,
    isAuthenticated: !!user && !!accessToken,
    isLoading,
    accessToken,
    login,
    logout,
    hasRole,
    hasAnyRole,
  };
}

// ── Dev / Mock Auth Hook ──

function useMockEntraAuth() {
  const { user, roles, accessToken, isLoading } = useSelector(selectAuth);
  const dispatch = useDispatch();
  const hasRole = useCallback((role: string) => roles.includes(role), [roles]);
  const hasAnyRole = useCallback((required: string[]) => required.some((role) => roles.includes(role)), [roles]);

  const login = useCallback(async (_credentials?: LoginCredentials) => {}, []);
  const logout = useCallback(async () => {
    dispatch(authActions.clear());
  }, [dispatch]);

  return { user, roles, isAuthenticated: !!user && !!accessToken, isLoading, accessToken, login, logout, hasRole, hasAnyRole };
}

function useDevAuth() {
  const { user, roles } = useSelector(selectAuth);
  const dispatch = useDispatch();
  const hasRole = useCallback((role: string) => roles.includes(role), [roles]);
  const hasAnyRole = useCallback((required: string[]) => required.some((role) => roles.includes(role)), [roles]);

  const login = useCallback(async (_credentials?: LoginCredentials) => {
    console.log('[Auth] login() is a no-op in mock mode');
  }, []);

  const logout = useCallback(async () => {
    dispatch(authActions.clear());
    window.location.href = portalHref('/');
  }, [dispatch]);

  return {
    user,
    roles,
    isAuthenticated: !!user,
    isLoading: false,
    accessToken: 'dev-access-token',
    login,
    logout,
    hasRole,
    hasAnyRole,
  };
}

// ── MSAL Auth Hook (production) ──

function useMsalAuth() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { user, roles } = useSelector(selectAuth);
  const dispatch = useDispatch();
  const hasRole = useCallback((role: string) => roles.includes(role), [roles]);
  const hasAnyRole = useCallback((required: string[]) => required.some((role) => roles.includes(role)), [roles]);

  const login = useCallback(
    async (_credentials?: LoginCredentials) => {
      await instance.loginPopup(loginRequest);
    },
    [instance],
  );

  const logout = useCallback(async () => {
    dispatch(authActions.clear());
    setAccessToken(null);
    await instance.logoutRedirect({
      account: instance.getActiveAccount() ?? undefined,
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance, dispatch]);

  return {
    user,
    roles,
    isAuthenticated: isAuthenticated && !!user && user.sub === (instance.getActiveAccount()?.localAccountId ?? instance.getActiveAccount()?.homeAccountId),
    isLoading: inProgress !== 'none',
    accessToken: getAccessToken(),
    login,
    logout,
    hasRole,
    hasAnyRole,
  };
}

// ── Primary Auth Hook (exported) ──

/**
 * Primary auth hook backed by the common Redux auth state.
 *
 * Usage:
 * ```tsx
 * const { user, isAuthenticated, login, logout, hasRole } = useAuth();
 * ```
 *
 * Mode is selected by `VITE_AUTH_MODE` (`local` | `entra`), or forced
 * to the seeded mock user by `VITE_DEV_AUTH=true`.
 */
export function useAuth() {
  /* eslint-disable react-hooks/rules-of-hooks */
  if (IS_DEV_AUTH) return useDevAuth();
  if (IS_LOCAL_AUTH) return useLocalAuth();
  if (IS_MOCK_ENTRA_AUTH) return useMockEntraAuth();
  return useMsalAuth();
  /* eslint-enable react-hooks/rules-of-hooks */
}
